import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import { AddEditCustomer, GetCustomerList } from "../../../utils/api.constant";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../state/toast/slice";
import CustomDrawer from "../../../components/drawer";
import CustomerForm from "./customer-form";
import { TCustomer } from "../../../types/index";
import { useNavigate } from "react-router-dom";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import ActionCard from "../../../components/actionCard/actionCard";
import {
  TContext,
  TFilterModel,
  TPrivilege,
  TResponseModel,
} from "../../../types/config";
import GenericPopover from "../../../components/pop-over";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  EditFilled,
  FilterFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import utils from "../../../utils";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import { defaultFilterParams } from "../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import CustomModal from "../../../components/modal";

const CUSTOMER_LIST_COLUMN_KEY = "customerList";

interface CustomerDataType {
  key: string;
  srNo: number;
  name: string;
  code: string;
  isActive: string;
  hidden?: boolean;
}

export default function CustomerDataList() {
  const { post } = useFetch();
  const navigate = useNavigate();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CustomerDataType[]>([]);
  const context: TContext = useContext(UserContext);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [selectedCustomer, setSelectedCustomer] = useState<TCustomer | null>(
    null
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const showDeleteModal = (user: TCustomer) => {
    setSelectedCustomer(user);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setSelectedCustomer(null);
    setIsDeleteModalVisible(false);
  };

  const handleDeleteOk = async () => {
    if (selectedCustomer) {
      await deleteCustomer(selectedCustomer);
    }
    setSelectedCustomer(null);
    setIsDeleteModalVisible(false);
  };

  const deleteCustomer = async (customer: TCustomer) => {
    try {
      const payload = {
        data: {
          id: customer.id,
          isDeleted: 1,
        },
      };
      const response = await post(AddEditCustomer, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // width: "50%",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: CustomerDataType) =>
        record.name?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      // width: "44%",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.code.localeCompare(b.code),
      onFilter: (value: string | number | boolean, record: CustomerDataType) =>
        record.code?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      width: 80,
      render: (record: TCustomer) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList as TPrivilege[],
                  ePrivileges.EDITCUSTOMERS
                ) ? (
                  <CustomDrawer
                    trigger={
                      <ActionCard text="Edit">
                        <EditFilled />
                      </ActionCard>
                    }
                    component={CustomerForm}
                    componentProps={{
                      id: record.id,
                      onRefreshList: () => fetchData(),
                    }}
                    title="Customer Form"
                    size="default"
                  />
                ) : null}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList as TPrivilege[],
                  ePrivileges.DELETECUSTOMERS
                ) ? (
                  <ActionCard
                    text="Delete"
                    onClick={() => showDeleteModal(record)}
                  >
                    <DeleteOutlined />
                  </ActionCard>
                ) : null}
              </div>
            }
            icon={
              <MoreOutlined
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  display:
                    Utils.isUserHasAccess(
                      context.user?.privilegeList as TPrivilege[],
                      ePrivileges.EDITCUSTOMERS
                    ) ||
                    Utils.isUserHasAccess(
                      context.user?.privilegeList as TPrivilege[],
                      ePrivileges.DELETECUSTOMERS
                    )
                      ? "visible"
                      : "none",
                }}
              />
            }
          />
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        CUSTOMER_LIST_COLUMN_KEY
      );
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col])
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Transform data to match table columns
        const formattedData = data.map((item: TCustomer, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          name: item.name,
          code: item.customerCode,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: CustomerDataType[]) => {
    const getFilters = (key: keyof CustomerDataType) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-"
            ) as string[]
        )
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };
    const nameFilters = getFilters("name");
    const codeFilters = getFilters("code");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "name") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "code") {
          return { ...col, filters: codeFilters };
        }
        // This line is the fix. It returns the original column object if no changes were made.
        return col;
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    const nameMatch =
      !activeFilters.name || activeFilters.name.includes(item.name);

    const codeMatch =
      !activeFilters.code || activeFilters.code.includes(item.code);

    return searchMatch && nameMatch && codeMatch;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(CUSTOMER_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(CUSTOMER_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item)))
  );

  const exportHeaders = visibleColumns
    ?.filter(
      (column) =>
        column?.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column?.title,
      key: column?.key,
    }));

  const exportData = data.map((user) => {
    const mapped = {
      ...user,
      isActive: user.isActive ? "Active" : "Inactive",
    };
    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Customers`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ADDCUSTOMERS
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={CustomerForm}
              componentProps={{
                id: 0,
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Customer Form"
              size="default"
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`customer_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content", // Adjust height dynamically
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm Delete"
        isOpen={isDeleteModalVisible}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{selectedCustomer?.name}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
